import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { DashboardCommercialDiscussionClientV2Dto } from '@/lib/api/v2/dashboard'

interface ArchivedSectionProps {
  items: DashboardCommercialDiscussionClientV2Dto[]
  onShowHistory: (item: DashboardCommercialDiscussionClientV2Dto) => void
}

const REASON_LABELS: Record<string, string> = {
  declined: 'Refusé',
  superseded: 'Remplacé',
  inactive: 'Inactif',
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('fr-BE', { day: '2-digit', month: '2-digit' }).format(
      new Date(iso),
    )
  } catch {
    return '—'
  }
}

/**
 * Collapsible "Archivés" section for the SOLY-SALES-001 Discussion commerciale view
 * (mini-scope 04 / Composant 5 of _design.md). Renders after the critical bucket when
 * archived items exist (urgencyBucket === "archived"). Fermée par défaut. Lignes grisées
 * + badge localisé selon archiveReason (declined / superseded / inactive).
 *
 * Hidden entirely when items.length === 0 — never an empty repliable.
 */
export function ArchivedSection({ items, onShowHistory }: ArchivedSectionProps) {
  const [open, setOpen] = useState(false)

  if (items.length === 0) return null

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="space-y-3"
      data-testid="archived-section"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-2 text-left text-sm hover:bg-muted/40"
          data-testid="archived-section-toggle"
        >
          <div className="flex items-center gap-2 text-muted-foreground">
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span className="font-medium">Archivés</span>
            <span className="text-xs">(≥60j d'inactivité ou refusés)</span>
          </div>
          <span className="text-xs font-medium text-muted-foreground">{items.length}</span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => {
          const reasonLabel = c.archiveReason
            ? REASON_LABELS[c.archiveReason] || c.archiveReason
            : 'Archivé'
          const sentLabel = formatShortDate(c.issuedAt)
          const relancedLabel = formatShortDate(c.lastFollowUpAt)
          const followUpCount = c.followUpCount ?? 0

          return (
            <Link
              key={c.customerId}
              to={`/clients/${c.customerId}`}
              className="block rounded-lg border border-dashed bg-muted/30 p-4 text-muted-foreground hover:bg-muted/50"
              data-testid="archived-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate text-foreground/70">{c.name || '—'}</div>
                </div>
                <Badge variant="outline" className="rounded-md shrink-0 text-xs">
                  {reasonLabel}
                </Badge>
              </div>

              <div className="mt-2 text-xs">
                Envoyé {sentLabel}
                <span className="mx-1.5 opacity-50">·</span>
                Relancé {relancedLabel}
              </div>

              {followUpCount > 0 && (
                <div className="mt-3 flex items-center justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onShowHistory(c)
                    }}
                    data-testid="archived-history-btn"
                  >
                    Historique ({followUpCount})
                  </Button>
                </div>
              )}
            </Link>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}
